import { useEffect, useCallback } from 'react';
import { useSSE } from '../app/providers/SseProvider';
import { EXECUTION_TRACE_MESSAGE_ADDED_SSE_EVENT } from '@terra/shared-contracts-agent-execution';

interface UseAgentExecutionSSEParams {
  executionId?: string;
  targetId?: string;
  enabled?: boolean;
  onTraceEvent?: (event: ExecutionTraceMessageSSEEvent['data']) => void;
}

type ExecutionTraceMessageSSEEvent = {
  type: 'EXECUTION_TRACE_MESSAGE_ADDED';
  data: {
    executionId: string;
    traceId: string;
    targetId: string;
    threadId: string;
  };
};

export function useAgentExecutionSSE({
  executionId,
  targetId,
  enabled = true,
  onTraceEvent,
}: UseAgentExecutionSSEParams) {
  const { subscribe } = useSSE();

  const handleEvent = useCallback(
    (event: ExecutionTraceMessageSSEEvent) => {
      if (targetId && event.data.targetId !== targetId) {
        return;
      }

      if (executionId && event.data.executionId !== executionId) {
        return;
      }

      onTraceEvent?.(event.data);
    },
    [targetId, executionId, onTraceEvent],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const unsubscribe = subscribe(
      EXECUTION_TRACE_MESSAGE_ADDED_SSE_EVENT,
      (event) => {
        handleEvent({
          type: 'EXECUTION_TRACE_MESSAGE_ADDED',
          data: event,
        });
      },
    );

    return () => {
      unsubscribe();
    };
  }, [subscribe, handleEvent, enabled]);
}
